// Enhanced ServiceList.js - Fixed CSS Layout with Larger Service Names and FIXED COLOR LOGIC
import React, { useState, useEffect } from 'react';
// REMOVED: import './EnhancedServiceList.css'; // This was causing conflicts

const ServiceList = ({ 
  servicesByCategory, 
  onEditService, 
  onDeleteService, 
  onCalculateService, 
  onDuplicateService,
  calculationResults, 
  selectedService, 
  formatTime, 
  onAddService,
  settings,
  stylists
}) => {
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [publicLinkModal, setPublicLinkModal] = useState(false);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [businessName, setBusinessName] = useState('');

const totalServices = Object.values(servicesByCategory).flat().length;

const handleDuplicateClick = (service) => {
  const newName = prompt(
    `Create a duplicate of "${service.name}".\n\nEnter the new service name:`,
    `${service.name} (Senior)`
  );
  
  if (newName && newName.trim()) {
    onDuplicateService(service.id, newName.trim());
  }
};

// GOLD STANDARD: Enhanced profit calculation with both amount and percentage - FIXED COLOR LOGIC
const getProfitabilityData = (service, businessSettings, availableStylists) => {
  console.log('=== GOLD STANDARD PROFITABILITY CALCULATION ===');
  console.log('Full service object:', service);
  
  // Get current price
  const currentPrice = parseFloat(service.Current_Cost || service.currentPrice || 0);
  
  if (!currentPrice || currentPrice === 0) {
    return {
      status: null,
      recommendedPrice: null,
      difference: null,
      profitMargin: null,
      profitAmount: null,
      profitClass: 'neutral',
      isLive: false
    };
  }
  
  // Use cached database values which include proper product costs
  const recommendedPrice = parseFloat(
    service.recommended_price || 
    service.calculatedPrice || 
    service.recommendedPrice || 
    service.rrp ||
    0
  );
  
  // FIXED: Get the operating costs (breakeven without owner profit)
  // This should be the total_cost which now represents operating costs only
  const operatingCosts = parseFloat(
    service.total_cost ||
    service.breakdown?.total_cost || 
    service.breakevenPrice || 
    service.breakeven || 
    0
  );
  
  const materialCost = parseFloat(
    service.product_cost ||
    service.productCost || 
    service.materialCost || 
    0
  );
  
  console.log('Database field mapping:');
  console.log('- service.total_cost (operating costs):', service.total_cost);
  console.log('- service.recommended_price:', service.recommended_price);
  console.log('- service.product_cost:', service.product_cost);
  console.log('- service.currentPrice:', service.currentPrice);
  console.log('- service.Current_Cost:', service.Current_Cost);
  
  console.log('Using database values:');
  console.log('- Recommended price:', recommendedPrice);
  console.log('- Operating costs:', operatingCosts);
  console.log('- Material cost:', materialCost);
  
  // GOLD STANDARD: Calculate profit at current price vs operating costs
  const costToUse = operatingCosts > 0 ? operatingCosts : materialCost;
  const difference = recommendedPrice > 0 ? recommendedPrice - currentPrice : null;
  
  // GOLD STANDARD: Profit calculation
  const profitAmount = costToUse > 0 ? currentPrice - costToUse : null;
  const profitMargin = costToUse > 0 && currentPrice > 0 ? (profitAmount / currentPrice) * 100 : null;
  
  // ADDED: Calculate profit at recommended price for comparison
  const recommendedProfitAmount = costToUse > 0 && recommendedPrice > 0 ? recommendedPrice - costToUse : null;
  const recommendedProfitMargin = costToUse > 0 && recommendedPrice > 0 ? (recommendedProfitAmount / recommendedPrice) * 100 : null;
  
  console.log('GOLD STANDARD Calculated values:');
  console.log('- Operating costs used:', costToUse);
  console.log('- Current price profit amount:', profitAmount);
  console.log('- Current price profit margin:', profitMargin);
  console.log('- Recommended profit amount:', recommendedProfitAmount);
  console.log('- Recommended profit margin:', recommendedProfitMargin);
  
  // FIXED: Status calculation for traffic light indicators
  let status = null;
  if (recommendedPrice > 0) {
    const priceRatio = currentPrice / recommendedPrice;
    
    if (priceRatio < 0.8) {
      status = { type: 'danger', icon: '🔴', color: '#dc3545' };
    } else if (priceRatio < 0.95) {
      status = { type: 'warning', icon: '🟡', color: '#ffc107' };
    } else if (priceRatio <= 1.05) {
      status = { type: 'success', icon: '🟢', color: '#28a745' };
    } else {
      status = { type: 'info', icon: '🔵', color: '#17a2b8' };
    }
  }
  
  // FIXED: Add profit classification for color coding
  let profitClass = 'neutral';
  if (profitAmount !== null) {
    if (profitAmount < 0) {
      profitClass = 'loss'; // Red for losses
    } else if (profitMargin < 10) {
      profitClass = 'low'; // Red for very low margins
    } else if (profitMargin < 20) {
      profitClass = 'ok'; // Yellow for acceptable margins
    } else {
      profitClass = 'good'; // Green for good margins
    }
  }
  
  return {
    status,
    recommendedPrice: recommendedPrice > 0 ? recommendedPrice : null,
    difference,
    profitMargin,
    profitAmount,
    profitClass, // NEW: Added profit classification
    recommendedProfitAmount,
    recommendedProfitMargin,
    totalCost: costToUse,
    operatingCosts: costToUse,
    isLive: false // Using database values, not live calculation
  };
};

// Legacy function for backwards compatibility
const getProfitabilityStatus = (service) => {
  const data = getProfitabilityData(service);
  return data.status;
};

  // ==========================================
  // INITIALIZATION
  // ==========================================

  useEffect(() => {
    // Load business name from localStorage or prompt user
    const storedBusinessName = localStorage.getItem('businessName');
    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
    } else {
      // Prompt user for business name on first use
      promptForBusinessName();
    }
  }, []);

  const promptForBusinessName = () => {
    const name = prompt(
      'Please enter your business name for branded exports:',
      'My Salon & Spa'
    );
    
    if (name && name.trim()) {
      const businessName = name.trim();
      setBusinessName(businessName);
      localStorage.setItem('businessName', businessName);
      updateBusinessNameOnServer(businessName);
    }
  };

  const updateBusinessNameOnServer = async (name) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      await fetch('/api/export/update-business-name', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ business_name: name })
      });
    } catch (error) {
      console.error('Failed to update business name:', error);
    }
  };

  // ==========================================
  // AUTHENTICATION & HELPERS
  // ==========================================

  const getAuthToken = () => {
    // Try both token storage keys to be compatible with different implementations
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      showError('Please log in to use export features');
      return null;
    }
    return token;
  };

  const showError = (message) => {
    setExportStatus(`❌ ${message}`);
    setTimeout(() => setExportStatus(''), 5000);
  };

  const showSuccess = (message) => {
    setExportStatus(`✅ ${message}`);
    setTimeout(() => setExportStatus(''), 3000);
  };

  const getBusinessName = () => {
    return businessName || 'Your Salon';
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // ==========================================
  // EXPORT FUNCTIONS - PHASE 3A (FIXED DATA FORMAT)
  // ==========================================

  // Helper function to transform data back to backend format for exports
  const transformServicesForExport = (servicesByCategory) => {
    const exportData = {};
    
    Object.entries(servicesByCategory).forEach(([categoryName, services]) => {
      exportData[categoryName] = services.map(service => ({
        name: service.name,
        price: service.currentPrice || 0,
        duration: service.time || 0,
        // Add other fields that might be needed for exports
        category: service.category || categoryName
      }));
    });
    
    return exportData;
  };

  const handleExportPDF = async () => {
    const token = getAuthToken();
    if (!token) return;

    setExportLoading(true);
    setExportStatus('📄 Generating professional PDF...');
    
    try {
      const response = await fetch('/api/export/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          services: transformServicesForExport(servicesByCategory)
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          showError('Session expired. Please log in again.');
          return;
        }
        
        const error = await response.json();
        if (error.message === 'Business name is required') {
          promptForBusinessName();
          return;
        }
        throw new Error(error.message || 'Export failed');
      }

      const blob = await response.blob();
      const filename = `${getBusinessName().replace(/[^a-zA-Z0-9]/g, '_')}_Price_List.pdf`;
      downloadFile(blob, filename);
      showSuccess('PDF exported successfully!');

    } catch (error) {
      console.error('PDF export error:', error);
      showError('PDF export failed. Please try again.');
    } finally {
      setExportLoading(false);
      setExportDropdownOpen(false);
    }
  };

  const handleCreatePublicLink = async () => {
    const token = getAuthToken();
    if (!token) return;

    setExportLoading(true);
    setExportStatus('🔗 Creating shareable link...');
    
    try {
      const response = await fetch('/api/export/public-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          services: transformServicesForExport(servicesByCategory),
          expiry_days: 30
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          showError('Session expired. Please log in again.');
          return;
        }
        
        const error = await response.json();
        if (error.message === 'Business name is required') {
          promptForBusinessName();
          return;
        }
        throw new Error(error.message || 'Link creation failed');
      }

      const data = await response.json();
      setPublicLinkData(data);
      setPublicLinkModal(true);
      setExportStatus('');

    } catch (error) {
      console.error('Public link error:', error);
      showError('Link creation failed. Please try again.');
    } finally {
      setExportLoading(false);
      setExportDropdownOpen(false);
    }
  };

  const handleExportExcel = async () => {
    const token = getAuthToken();
    if (!token) return;

    setExportLoading(true);
    setExportStatus('📊 Generating business analysis...');
    
    try {
      const response = await fetch('/api/export/excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          services: transformServicesForExport(servicesByCategory)
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          showError('Session expired. Please log in again.');
          return;
        }
        
        const error = await response.json();
        if (error.message === 'Business name is required') {
          promptForBusinessName();
          return;
        }
        throw new Error(error.message || 'Export failed');
      }

      const blob = await response.blob();
      const filename = `${getBusinessName().replace(/[^a-zA-Z0-9]/g, '_')}_Business_Analysis.xlsx`;
      downloadFile(blob, filename);
      showSuccess('Excel exported successfully!');

    } catch (error) {
      console.error('Excel export error:', error);
      showError('Excel export failed. Please try again.');
    } finally {
      setExportLoading(false);
      setExportDropdownOpen(false);
    }
  };

  const handleExportSocialImage = async () => {
    const token = getAuthToken();
    if (!token) return;

    setExportLoading(true);
    setExportStatus('📱 Creating social media image...');
    
    try {
      const response = await fetch('/api/export/social-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          services: transformServicesForExport(servicesByCategory)
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          showError('Session expired. Please log in again.');
          return;
        }
        
        const error = await response.json();
        if (error.message === 'Business name is required') {
          promptForBusinessName();
          return;
        }
        throw new Error(error.message || 'Export failed');
      }

      const blob = await response.blob();
      const filename = `${getBusinessName().replace(/[^a-zA-Z0-9]/g, '_')}_Social_Media.png`;
      downloadFile(blob, filename);
      showSuccess('Social image exported successfully!');

    } catch (error) {
      console.error('Social image export error:', error);
      showError('Social image export failed. Please try again.');
    } finally {
      setExportLoading(false);
      setExportDropdownOpen(false);
    }
  };

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Link copied to clipboard! 📋');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSuccess('Link copied to clipboard! 📋');
    }
  };

  const shareViaAPI = async (url, title) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url,
        });
      } catch (error) {
        console.log('Share cancelled or failed');
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

   // ==========================================
  // RENDER COMPONENT
  // ==========================================

return (
  <>
   <style>{`
  /* RESET - Override all external CSS completely */
  .service-list-container * {
    box-sizing: border-box !important;
  }
  
  /* SERVICE LIST CONTAINER */
  .service-list-container {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 20px !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    background: #f8f9fa !important;
  }
  
  /* HEADER SECTION */
  .service-list-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 30px !important;
    padding: 20px !important;
    background: white !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
  }
  
  .header-left h2 {
    margin: 0 0 5px 0 !important;
    font-size: 24px !important;
    font-weight: 700 !important;
    color: #1a1a1a !important;
  }
  
  .header-left p {
    margin: 0 !important;
    font-size: 14px !important;
    color: #6c757d !important;
  }
  
  .header-right {
    display: flex !important;
    align-items: center !important;
    gap: 15px !important;
  }
  
  /* STATUS LEGEND PANEL */
  .status-legend-panel {
    background: white !important;
    border-radius: 12px !important;
    padding: 25px !important;
    margin-bottom: 30px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
    border-left: 4px solid #007bff !important;
  }
  
  .status-legend-panel h3 {
    margin: 0 0 20px 0 !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
  }
  
  .legend-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
    gap: 15px !important;
    margin-bottom: 20px !important;
  }
  
  .legend-item {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 12px !important;
    border-radius: 8px !important;
    background: #f8f9fa !important;
  }
  
  .legend-icon {
    font-size: 18px !important;
    min-width: 20px !important;
  }
  
  .legend-item strong {
    display: block !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    margin-bottom: 3px !important;
  }
  
  .legend-item small {
    font-size: 11px !important;
    color: #6c757d !important;
    line-height: 1.3 !important;
  }
  
  .legend-note {
    background: #e3f2fd !important;
    padding: 15px !important;
    border-radius: 8px !important;
    border-left: 3px solid #2196f3 !important;
  }
  
  .legend-note strong {
    font-size: 13px !important;
    color: #1565c0 !important;
  }
  
  /* CATEGORY SECTIONS */
  .category-section {
    margin-bottom: 40px !important;
  }
  
  .category-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 15px !important;
    padding: 0 5px !important;
  }
  
  .category-header h3 {
    margin: 0 !important;
    font-size: 20px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
  }
  
  .add-service-btn {
    background: #007bff !important;
    color: white !important;
    border: none !important;
    padding: 8px 16px !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
  }
  
  .add-service-btn:hover {
    background: #0056b3 !important;
    transform: translateY(-1px) !important;
  }
  
  /* SERVICES TABLE - FIXED GRID LAYOUT */
  .services-table {
    background: white !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
    overflow: hidden !important;
    border: 1px solid #e9ecef !important;
  }
  
  /* TABLE GRID - BALANCED LAYOUT WITH MORE SPACE FOR ACTIONS */
  .table-header,
  .table-row {
    display: grid !important;
    grid-template-columns: 1.6fr 1fr 0.8fr 0.9fr 0.9fr 1fr 0.8fr 0.6fr 1.5fr !important;
    gap: 0 !important;
    align-items: center !important;
    width: 100% !important;
  }
  
  /* CELL STYLING - INCREASED PADDING AND HEIGHT */
  .table-row > div,
  .table-header > div {
    padding: 15px 10px !important;
    font-size: 14px !important;
    height: 65px !important;
    line-height: 1.4 !important;
    display: flex !important;
    align-items: center !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    min-width: 0 !important;
    border-right: 1px solid #f1f3f4 !important;
  }
  
  /* HEADER SPECIFIC STYLING */
  .table-header {
    background: #f8f9fa !important;
    border-bottom: 2px solid #dee2e6 !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
  }
  
  .table-header > div {
    height: 55px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    color: #495057 !important;
    background: #f8f9fa !important;
  }
  
  /* ROW STYLING */
  .table-row {
    border-bottom: 1px solid #f1f3f4 !important;
    transition: all 0.2s ease !important;
  }
  
  .table-row:hover {
    background: #f8f9fa !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }
  
  .table-row.selected {
    background: #e3f2fd !important;
    border-color: #2196f3 !important;
  }
  
  /* SERVICE NAME COLUMN - BALANCED SIZE */
  .col-name {
    padding-right: 12px !important;
    justify-content: flex-start !important;
  }
  
  .col-name strong {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
    line-height: 1.3 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    max-width: 100% !important;
  }
  
  /* DURATION COLUMN */
  .col-duration {
    font-size: 13px !important;
    color: #6c757d !important;
    text-align: left !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  
  /* PRICE COLUMNS */
  .col-cost,
  .col-price,
  .col-recommended {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace !important;
    text-align: right !important;
    justify-content: flex-end !important;
  }
  
  .col-price strong {
    font-size: 15px !important;
    font-weight: 700 !important;
    color: #007bff !important;
  }
  
  .recommended-price {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #28a745 !important;
  }
  
  .no-calculation {
    font-size: 11px !important;
    color: #9ca3af !important;
    font-style: italic !important;
  }
  
  /* PROFIT COLUMN STYLING - GOLD STANDARD WITH FIXED COLORS */
  .profit-margin {
    font-size: 12px !important;
    padding: 6px 8px !important;
    border-radius: 4px !important;
    font-weight: 600 !important;
    text-align: center !important;
    display: block !important;
    min-width: 75px !important;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace !important;
  }
  
  .profit-margin small {
    font-size: 10px !important;
    display: block !important;
    margin-top: 2px !important;
    opacity: 0.8 !important;
    font-weight: 500 !important;
  }
  
  /* FIXED: Proper color logic - no more red positives! */
  .profit-margin.good {
    background: #d4edda !important;
    color: #155724 !important;
  }
  
  .profit-margin.ok {
    background: #fff3cd !important;
    color: #856404 !important;
  }
  
  .profit-margin.low,
  .profit-margin.loss {
    background: #f8d7da !important;
    color: #721c24 !important;
  }
  
  .profit-margin.neutral {
    background: #f8f9fa !important;
    color: #6c757d !important;
  }
  
  /* DIFFERENCE COLUMN */
  .difference {
    font-size: 12px !important;
    padding: 6px 8px !important;
    border-radius: 4px !important;
    font-weight: 600 !important;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace !important;
  }
  
  .difference.positive {
    background: #d4edda !important;
    color: #155724 !important;
  }
  
  .difference.negative {
    background: #f8d7da !important;
    color: #721c24 !important;
  }
  
  /* Ensure zero differences are neutral */
  .difference:not(.positive):not(.negative) {
    background: #f8f9fa !important;
    color: #6c757d !important;
  }
  
  /* STATUS INDICATORS */
  .status-indicator {
    font-size: 16px !important;
    width: 20px !important;
    text-align: center !important;
    justify-content: center !important;
  }
  
  /* ACTION BUTTONS - MORE SPACE AND BETTER LAYOUT */
  .col-actions {
    display: flex !important;
    gap: 6px !important;
    flex-wrap: nowrap !important;
    justify-content: flex-end !important;
    padding: 8px !important;
    min-width: 140px !important;
  }
  
  .btn-edit,
  .btn-duplicate,
  .btn-delete {
    padding: 6px 8px !important;
    font-size: 10px !important;
    min-width: 38px !important;
    border-radius: 4px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
    border: none !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
    flex-shrink: 0 !important;
  }
  
  .btn-edit {
    background: #007bff !important;
    color: white !important;
  }
  
  .btn-edit:hover {
    background: #0056b3 !important;
  }
  
  .btn-duplicate {
    background: #6c757d !important;
    color: white !important;
  }
  
  .btn-duplicate:hover {
    background: #545b62 !important;
  }
  
  .btn-delete {
    background: #dc3545 !important;
    color: white !important;
  }
  
  .btn-delete:hover {
    background: #c82333 !important;
  }
  
  /* EXPORT FUNCTIONALITY STYLES */
  .business-name-btn {
    background: #f8f9fa !important;
    border: 1px solid #dee2e6 !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    font-size: 12px !important;
    color: #495057 !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
  }
  
  .business-name-btn:hover {
    background: #e9ecef !important;
  }
  
  .export-dropdown {
    position: relative !important;
    display: inline-block !important;
  }
  
  .export-button {
    background: #28a745 !important;
    color: white !important;
    border: none !important;
    padding: 10px 16px !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    transition: all 0.2s !important;
  }
  
  .export-button:hover {
    background: #218838 !important;
    transform: translateY(-1px) !important;
  }
  
  .export-button:disabled {
    background: #6c757d !important;
    cursor: not-allowed !important;
    transform: none !important;
  }
  
  .export-menu {
    position: absolute !important;
    top: 100% !important;
    right: 0 !important;
    background: white !important;
    border: 1px solid #dee2e6 !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    z-index: 1000 !important;
    min-width: 280px !important;
    margin-top: 5px !important;
  }
  
  .export-option {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    width: 100% !important;
    padding: 12px 16px !important;
    border: none !important;
    background: none !important;
    text-align: left !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
    border-bottom: 1px solid #f1f3f4 !important;
  }
  
  .export-option:last-child {
    border-bottom: none !important;
  }
  
  .export-option:hover {
    background: #f8f9fa !important;
  }
  
  .export-option:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }
  
  .option-icon {
    font-size: 18px !important;
    min-width: 20px !important;
  }
  
  .export-option strong {
    display: block !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
    margin-bottom: 2px !important;
  }
  
  .export-option small {
    display: block !important;
    font-size: 11px !important;
    color: #6c757d !important;
  }
  
  .export-status {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    font-size: 12px !important;
    font-weight: 500 !important;
  }
  
  .export-status.loading {
    background: #fff3cd !important;
    color: #856404 !important;
  }
  
  .export-status.success {
    background: #d4edda !important;
    color: #155724 !important;
  }
  
  .spinner {
    width: 12px !important;
    height: 12px !important;
    border: 2px solid #f3f3f3 !important;
    border-top: 2px solid #856404 !important;
    border-radius: 50% !important;
    animation: spin 1s linear infinite !important;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* CALCULATION RESULTS PANEL */
  .calculation-results {
    background: white !important;
    border-radius: 12px !important;
    padding: 25px !important;
    margin-top: 30px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
    border-left: 4px solid #28a745 !important;
  }
  
  .calculation-results h3 {
    margin: 0 0 20px 0 !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
  }
  
  .results-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
    gap: 20px !important;
  }
  
  .result-item {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 15px !important;
    background: #f8f9fa !important;
    border-radius: 8px !important;
  }
  
  .result-item label {
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #495057 !important;
  }
  
  .result-item span {
    font-size: 16px !important;
    font-weight: 700 !important;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace !important;
  }
  
  /* MODAL STYLES */
  .modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background: rgba(0, 0, 0, 0.5) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 2000 !important;
    padding: 20px !important;
  }
  
  .modal-content {
    background: white !important;
    border-radius: 12px !important;
    max-width: 500px !important;
    width: 100% !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
  }
  
  .modal-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 20px 25px !important;
    border-bottom: 1px solid #dee2e6 !important;
  }
  
  .modal-header h3 {
    margin: 0 !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
  }
  
  .modal-close {
    background: none !important;
    border: none !important;
    font-size: 24px !important;
    color: #6c757d !important;
    cursor: pointer !important;
    padding: 0 !important;
    width: 30px !important;
    height: 30px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .modal-close:hover {
    color: #495057 !important;
  }
  
  .modal-body {
    padding: 25px !important;
  }
  
  .modal-body p {
    margin: 0 0 20px 0 !important;
    font-size: 14px !important;
    color: #495057 !important;
    line-height: 1.5 !important;
  }
  
  .link-container {
    display: flex !important;
    gap: 10px !important;
    margin-bottom: 20px !important;
  }
  
  .link-input {
    flex: 1 !important;
    padding: 10px 12px !important;
    border: 1px solid #dee2e6 !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace !important;
    background: #f8f9fa !important;
  }
  
  .copy-button {
    background: #007bff !important;
    color: white !important;
    border: none !important;
    padding: 10px 16px !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    white-space: nowrap !important;
  }
  
  .copy-button:hover {
    background: #0056b3 !important;
  }
  
  .link-details {
    background: #f8f9fa !important;
    padding: 15px !important;
    border-radius: 8px !important;
    margin-bottom: 20px !important;
  }
  
  .link-details p {
    margin: 0 0 8px 0 !important;
    font-size: 12px !important;
  }
  
  .link-details p:last-child {
    margin-bottom: 0 !important;
  }
  
  .share-buttons {
    display: flex !important;
    gap: 10px !important;
  }
  
  .share-button,
  .preview-button {
    flex: 1 !important;
    padding: 12px 16px !important;
    border: none !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
  }
  
  .share-button {
    background: #28a745 !important;
    color: white !important;
  }
  
  .share-button:hover {
    background: #218838 !important;
  }
  
  .preview-button {
    background: #6c757d !important;
    color: white !important;
  }
  
  .preview-button:hover {
    background: #545b62 !important;
  }

  /* EMPTY STATE STYLES */
  .empty-state {
    background: white !important;
    border-radius: 12px !important;
    padding: 60px 40px !important;
    text-align: center !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
    margin-top: 20px !important;
  }

  .empty-state h3 {
    margin: 0 0 15px 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: #1a1a1a !important;
  }

  .empty-state p {
    color: #6c757d !important;
    font-size: 16px !important;
    margin-bottom: 30px !important;
    line-height: 1.5 !important;
  }

  .empty-state .add-service-btn {
    font-size: 15px !important;
    padding: 14px 28px !important;
    display: inline-block !important;
  }
  
  /* RESPONSIVE DESIGN */
  @media (max-width: 1200px) {
    .table-header,
    .table-row {
      grid-template-columns: 1.4fr 0.9fr 0.7fr 0.8fr 0.8fr 0.9fr 0.7fr 0.5fr 1.3fr !important;
    }
  }
  
  @media (max-width: 768px) {
    .service-list-header {
      flex-direction: column !important;
      gap: 15px !important;
      align-items: stretch !important;
    }
    
    .header-right {
      justify-content: center !important;
    }
    
    .table-header,
    .table-row {
      grid-template-columns: 1.4fr 0.7fr 0.6fr 0.7fr 0.7fr 0.8fr 0.6fr 0.4fr 0.9fr !important;
    }
    
    .table-row > div,
    .table-header > div {
      padding: 10px 6px !important;
      font-size: 12px !important;
      height: 55px !important;
    }
    
    .col-name strong {
      font-size: 13px !important;
    }
    
    .legend-grid {
      grid-template-columns: 1fr !important;
    }

    .empty-state {
      padding: 40px 20px !important;
    }

    .empty-state h3 {
      font-size: 20px !important;
    }

    .empty-state p {
      font-size: 14px !important;
    }
  }
`}</style>
    <div className="service-list-container">
      {/* Header with Export Functionality */}
      <div className="service-list-header">
        <div className="header-left">
          <h2>Service Management ({totalServices} services)</h2>
          <p>Professional pricing tools for {getBusinessName()}</p>
        </div>
        
        <div className="header-right">
          {/* Export Status */}
          {exportStatus && (
            <div className={`export-status ${exportLoading ? 'loading' : 'success'}`}>
              {exportLoading && <div className="spinner"></div>}
              {exportStatus}
            </div>
          )}
          
          {/* Business Name Settings */}
          <button 
            className="business-name-btn"
            onClick={promptForBusinessName}
            title="Update business name for exports"
          >
            🏢 {getBusinessName()}
          </button>
          
          {/* Export Dropdown */}
          <div className="export-dropdown">
            <button 
              className="export-button"
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              disabled={exportLoading}
            >
              <span className="export-icon">📊</span>
              Export Price List
              <span className="dropdown-arrow">▼</span>
            </button>
            
            {exportDropdownOpen && (
              <div className="export-menu">
                <button 
                  onClick={handleExportPDF}
                  className="export-option"
                  disabled={exportLoading}
                >
                  <span className="option-icon">📄</span>
                  <div>
                    <strong>Professional PDF</strong>
                    <small>Branded price list document</small>
                  </div>
                </button>
                
                <button 
                  onClick={handleCreatePublicLink}
                  className="export-option"
                  disabled={exportLoading}
                >
                  <span className="option-icon">🔗</span>
                  <div>
                    <strong>Shareable Link</strong>
                    <small>Public web page (30 days)</small>
                  </div>
                </button>
                
                <button 
                  onClick={handleExportExcel}
                  className="export-option"
                  disabled={exportLoading}
                >
                  <span className="option-icon">📊</span>
                  <div>
                    <strong>Business Analysis</strong>
                    <small>Excel with profit insights</small>
                  </div>
                </button>
                
                <button 
                  onClick={handleExportSocialImage}
                  className="export-option"
                  disabled={exportLoading}
                >
                  <span className="option-icon">📱</span>
                  <div>
                    <strong>Social Media Image</strong>
                    <small>Instagram/Facebook ready</small>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Status Legend Panel */}
      <div className="status-legend-panel">
        <h3>📊 Pricing Status Guide</h3>
        <div className="legend-grid">
          <div className="legend-item danger">
            <span className="legend-icon">🔴</span>
            <div>
              <strong>Severely Underpriced</strong>
              <small>Current price is less than 80% of recommended - significant losses</small>
            </div>
          </div>
          <div className="legend-item warning">
            <span className="legend-icon">🟡</span>
            <div>
              <strong>Underpriced</strong>
              <small>Current price is 80-95% of recommended - below target margins</small>
            </div>
          </div>
          <div className="legend-item success">
            <span className="legend-icon">🟢</span>
            <div>
              <strong>Well Priced</strong>
              <small>Current price is 95-105% of recommended - optimal pricing</small>
            </div>
          </div>
          <div className="legend-item info">
            <span className="legend-icon">🔵</span>
            <div>
              <strong>Above Target</strong>
              <small>Current price is above 105% of recommended - premium pricing</small>
            </div>
          </div>
        </div>
        <div className="legend-note">
          <strong>💰 Profit Explanation:</strong> Profit = Current Price - Operating Costs (materials + staff + overhead + support staff). Click "Calculate" to update values when business settings change.
        </div>
      </div>

      {/* EMPTY STATE OR SERVICES BY CATEGORY */}
      {Object.keys(servicesByCategory).length === 0 ? (
        <div className="empty-state">
          <h3>🎯 Welcome to Your Service Management</h3>
          <p>
            You haven't added any services yet. Let's get started by creating your first service!<br />
            Click the button below to add your first service category and service.
          </p>
          <button 
            className="add-service-btn"
            onClick={() => onAddService('General')}
          >
            + Add Your First Service
          </button>
        </div>
      ) : (
        /* Services by Category */
        Object.entries(servicesByCategory).map(([categoryName, services]) => (
          <div key={categoryName} className="category-section">
            <div className="category-header">
              <h3>{categoryName}</h3>
              <button 
                className="add-service-btn"
                onClick={() => onAddService(categoryName)}
              >
                + Add Service
              </button>
            </div>

            <div className="services-table">
              <div className="table-header">
                <div className="col-name">Service Name</div>
                <div className="col-duration">Duration</div>
                <div className="col-cost">Materials</div>
                <div className="col-price">Current Price</div>
                <div className="col-recommended">Recommended</div>
                <div className="col-profit-margin">Profit</div>
                <div className="col-difference">Difference</div>
                <div className="col-status">Status</div>
                <div className="col-actions">Actions</div>
              </div>

              {services.map((service) => {
                const profitData = getProfitabilityData(service, settings, stylists);
                const isSelected = selectedService === service.id;
                
                // Debug logging to check for data leakage
                console.log(`Service ${service.name} (ID: ${service.id}):`, {
                  currentPrice: service.currentPrice,
                  recommendedFromData: profitData.recommendedPrice,
                  isLive: profitData.isLive,
                  totalCost: profitData.totalCost,
                  profitClass: profitData.profitClass,
                  service: service
                });
                
                return (
                  <div key={service.id} className={`table-row ${isSelected ? 'selected' : ''}`}>
                    <div className="col-name">
                      <strong>{service.name}</strong>
                    </div>
                    <div className="col-duration">
                      {formatTime ? formatTime(service.time) : `${service.time || 0} mins`}
                    </div>
                    <div className="col-cost">
                      £{service.productCost ? parseFloat(service.productCost).toFixed(2) : '0.00'}
                    </div>
                    <div className="col-price">
                      <strong>£{service.currentPrice ? parseFloat(service.currentPrice).toFixed(2) : '0.00'}</strong>
                    </div>
                    <div className="col-recommended">
                      {profitData.recommendedPrice ? (
                        <span className="recommended-price">
                          £{profitData.recommendedPrice.toFixed(2)}
                        </span>
                      ) : (
                        <span className="no-calculation">Not calculated</span>
                      )}
                    </div>
                    <div className="col-profit-margin">
                      {profitData.profitMargin !== null && profitData.profitAmount !== null ? (
                        <span className={`profit-margin ${profitData.profitClass}`}>
                          £{profitData.profitAmount.toFixed(2)}
                          <br />
                          <small>({profitData.profitMargin.toFixed(1)}%)</small>
                        </span>
                      ) : (
                        <span className="no-calculation">-</span>
                      )}
                    </div>
                    <div className="col-difference">
                      {profitData.difference !== null ? (
                        <span className={`difference ${profitData.difference > 0 ? 'positive' : 'negative'}`}>
                          {profitData.difference > 0 ? '+' : ''}£{profitData.difference.toFixed(2)}
                        </span>
                      ) : (
                        <span className="no-calculation">-</span>
                      )}
                    </div>
                    <div className="col-status">
                      {profitData.status && (
                        <span className={`status-indicator ${profitData.status.type}`} title={profitData.status.type}>
                          {profitData.status.icon}
                        </span>
                      )}
                    </div>
                    <div className="col-actions">
                      <button onClick={() => onEditService(service.id)} className="btn-edit">
                        Edit
                      </button>
                      <button onClick={() => handleDuplicateClick(service)} className="btn-duplicate">
                        Dup
                      </button>
                      <button onClick={() => onDeleteService(service.id)} className="btn-delete">
                        Del
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Calculation Results Panel */}
      {calculationResults && (
        <div className="calculation-results">
          <h3>💰 Pricing Calculation Results</h3>
          <div className="results-grid">
            <div className="result-item">
              <label>Total Material Cost:</label>
              <span>£{calculationResults.totalCost?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="result-item">
              <label>Recommended Price:</label>
              <span className="recommended-price">£{calculationResults.recommendedPrice?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="result-item">
              <label>Profit Margin:</label>
              <span className={`profit-margin ${calculationResults.profitMargin > 50 ? 'good' : 'warning'}`}>
                {calculationResults.profitMargin?.toFixed(1) || '0'}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Public Link Modal */}
      {publicLinkModal && publicLinkData && (
        <div className="modal-overlay" onClick={() => setPublicLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔗 Shareable Price List Created!</h3>
              <button className="modal-close" onClick={() => setPublicLinkModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Your public price list is ready to share! Anyone with this link can view your pricing.</p>
              
              <div className="link-container">
                <input 
                  type="text" 
                  value={publicLinkData.public_url} 
                  readOnly 
                  className="link-input"
                />
                <button 
                  onClick={() => copyToClipboard(publicLinkData.public_url)}
                  className="copy-button"
                >
                  📋 Copy
                </button>
              </div>
              
              <div className="link-details">
                <p><strong>Expires:</strong> {new Date(publicLinkData.expires_at).toLocaleDateString()}</p>
                <p><strong>Link ID:</strong> {publicLinkData.public_id}</p>
              </div>
              
              <div className="share-buttons">
                <button 
                  onClick={() => shareViaAPI(publicLinkData.public_url, `${getBusinessName()} - Price List`)}
                  className="share-button"
                >
                  📱 Share
                </button>
                <button 
                  onClick={() => window.open(publicLinkData.public_url, '_blank')}
                  className="preview-button"
                >
                  👁️ Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </>
);
};

export default ServiceList;